import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTime(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function calculateHours(start: Date | string, end: Date | string): number {
  const startTime = new Date(start);
  const endTime = new Date(end);
  const diffMs = endTime.getTime() - startTime.getTime();
  return Math.round((diffMs / (1000 * 60 * 60)) * 100) / 100;
}

export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function getRoleColor(role: string): string {
  switch (role) {
    case 'ADMIN':
    case 'SUPERADMIN':
      return 'bg-red-100 text-red-800';
    case 'HR':
      return 'bg-blue-100 text-blue-800';
    case 'EMPLOYEE':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'present':
      return 'bg-green-100 text-green-800';
    case 'absent':
      return 'bg-red-100 text-red-800';
    case 'half_day':
      return 'bg-yellow-100 text-yellow-800';
    case 'late':
      return 'bg-orange-100 text-orange-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Check if we're in a secure context (HTTPS or localhost)
function isSecureContext(): boolean {
  return window.isSecureContext || 
         window.location.protocol === 'https:' || 
         window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.endsWith('.local');
}

// Get device location with fallback strategies
export function getCurrentLocation(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    console.log('🔍 Checking location availability...');
    console.log('🔒 Secure context:', isSecureContext());
    console.log('🌐 Protocol:', window.location.protocol);
    console.log('🏠 Hostname:', window.location.hostname);

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      console.error('❌ Geolocation not supported');
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }
    

    // Check secure context for production
    if (!isSecureContext() && window.location.protocol !== 'http:') {
      console.error('❌ Geolocation requires HTTPS or localhost');
      reject(new Error('Geolocation requires a secure connection (HTTPS) or localhost.'));
      return;
    }

    // Different options based on environment
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
    
    const options = {
      enableHighAccuracy: !isLocalhost, // Use high accuracy on production
      timeout: isLocalhost ? 30000 : 15000, // Longer timeout for localhost
      maximumAge: isLocalhost ? 300000 : 60000 // 5 min cache for localhost, 1 min for production
    };

    console.log('⚙️ Geolocation options:', options);
    console.log('📍 Requesting geolocation...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('✅ Geolocation success!');
        console.log('📍 Full position object:', position);
        console.log('�️ Coordinates:', {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp).toISOString()
        });
        
        // Validate coordinates
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          resolve({
            latitude: lat,
            longitude: lng,
          });
        } else {
          console.error('❌ Invalid coordinates:', { lat, lng });
          reject(new Error('Invalid location coordinates received.'));
        }
      },
      (error) => {
        console.error('❌ Geolocation error:', error);
        console.error('📊 Error details:', {
          code: error.code,
          message: error.message,
          timestamp: new Date().toISOString()
        });
        
        let errorMessage = 'Unable to get your location. ';
        
        switch (error.code) {
          case 1: // PERMISSION_DENIED
            errorMessage += isLocalhost 
              ? 'Location access was denied. Please allow location access in your browser settings.'
              : 'Location access was denied. Please enable location permissions for this site.';
            break;
          case 2: // POSITION_UNAVAILABLE
            errorMessage += 'Location information is unavailable. Please check your device settings.';
            break;
          case 3: // TIMEOUT
            errorMessage += 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage += `An unknown error occurred. Error code: ${error.code}`;
            break;
        }
        
        reject(new Error(errorMessage));
      },
      options
    );
  });
}

// Smart location function with multiple fallback strategies
export function getLocationWithFallback(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('User location:', position.coords);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      }
    );
  });
}

// // Alternative function that always works (for testing purposes)
// export function getMockLocation(): Promise<{ latitude: number; longitude: number }> {
//   console.log('🎭 Using mock location for testing');
  
//   // Return a location close to Kolkata for testing
//   return Promise.resolve({
//     latitude: 22.5726,
//     longitude: 88.3639
//   });
// }

// Function to test location permissions
export function checkLocationPermission(): Promise<string> {
  return new Promise((resolve) => {
    if (!navigator.permissions) {
      resolve('unknown - permissions API not supported');
      return;
    }
    
    navigator.permissions.query({ name: 'geolocation' })
      .then((permission) => {
        console.log('🔐 Location permission status:', permission.state);
        resolve(permission.state);
      })
      .catch(() => {
        resolve('unknown - query failed');
      });
  });
}

// Comprehensive location diagnostics
export async function diagnoseLocationIssues(): Promise<void> {
  console.log('🔍 === LOCATION DIAGNOSTICS ===');
  
  // Environment info
  console.log('🌐 Environment Info:');
  console.log('  - Protocol:', window.location.protocol);
  console.log('  - Hostname:', window.location.hostname);
  console.log('  - Secure Context:', isSecureContext());
  console.log('  - User Agent:', navigator.userAgent);
  
  // Geolocation support
  console.log('📍 Geolocation Support:');
  console.log('  - Available:', !!navigator.geolocation);
  
  // Permission status
  console.log('🔐 Permission Status:');
  try {
    const permission = await checkLocationPermission();
    console.log('  - Status:', permission);
  } catch (error) {
    console.log('  - Status: Error checking permissions');
  }
  
  // Test location access
  console.log('🧪 Testing Location Access:');
  try {
    const location = await getCurrentLocation();
    console.log('  - ✅ Success:', location);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.log('  - ❌ Failed:', errorMessage);
  }
  
  console.log('🔍 === DIAGNOSTICS COMPLETE ===');
}